import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { queryKeys, useApiMutation, useApiQuery } from '../api/hooks';
import { ApiError } from '../api/problem';
import type {
  BranchResponse,
  ChangeRoleRequest,
  InviteStaffRequest,
  InviteStaffResponse,
  ResetPasswordRequest,
  RoleTemplate,
  StaffMemberResponse,
} from '../api/types';
import { AsyncSection } from '../components/AsyncSection';
import { PageHeader } from '../components/PageHeader';
import { ErrorState } from '../components/states/StateViews';
import { useAuthStore } from '../auth/authStore';
import { MINIMUM_PASSWORD_LENGTH } from '../auth/passwords';
import { isReleased } from '../config/release';
import { useLocalisedName } from '../i18n/localisedName';

/**
 * The roles a clinic can give. Platform roles are not among them (SEC-003), and neither is clinic
 * administrator: a clinic has exactly one, and Laseera sets it (CR-15).
 */
const ASSIGNABLE_ROLES: readonly RoleTemplate[] = ['BRANCH_MANAGER', 'RECEPTIONIST', 'DOCTOR'];

/** Roles tied to one branch (ASM-009); the rest cover the whole clinic. */
const BRANCH_SCOPED: readonly RoleTemplate[] = ['BRANCH_MANAGER', 'RECEPTIONIST'];

function isBranchScoped(role: RoleTemplate | undefined): boolean {
  return role !== undefined && BRANCH_SCOPED.includes(role);
}

/**
 * Staff and roles (FR-105, FR-106).
 *
 * The administrator chooses each new person's first password and hands it over themselves: until
 * notifications arrive in M5 there is nothing to send it with, and the screen says so plainly
 * rather than implying a message went out. The person can change it from their own account.
 *
 * The password field is plain text on purpose. It is not the administrator's own secret but one
 * they are about to pass on, so seeing what they typed is what keeps a typo from locking someone
 * out on their first morning — and a password-type field would invite the browser to save it
 * against the administrator's own sign-in.
 */
export function StaffPage() {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const clinicId = useAuthStore((state) => state.activeClinicId);
  const permissions = useAuthStore((state) => state.permissions);
  const canManage = permissions.has('STAFF_MANAGE');
  const canAssignRoles = permissions.has('ROLE_ASSIGN');
  // A doctor's row leads to their working hours, for whoever may set them (D-2).
  const canSetHours = permissions.has('AVAILABILITY_MANAGE_ANY') && isReleased('doctorHours');
  const showActions = canAssignRoles || canManage || canSetHours;
  const ownAccountId = useAuthStore((state) => state.principal?.accountId);
  // A branch manager sees their own branch's people and the doctors (CR-16), so the heading says so.
  const branchView = useAuthStore(
    (state) =>
      state.principal?.memberships?.find((membership) => membership.clinicId === state.activeClinicId)?.role ===
      'BRANCH_MANAGER',
  );

  const [inviting, setInviting] = useState(false);
  const [newRole, setNewRole] = useState<RoleTemplate>('RECEPTIONIST');
  const [issued, setIssued] = useState<InviteStaffResponse | null>(null);
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [resetFor, setResetFor] = useState<string | null>(null);
  const [changingId, setChangingId] = useState<string | null>(null);

  const staff = useApiQuery<StaffMemberResponse[]>(
    queryKeys.staff(clinicId ?? ''),
    `/clinics/${clinicId}/staff`,
    { enabled: Boolean(clinicId) },
  );

  const branches = useApiQuery<BranchResponse[]>(
    queryKeys.branches(clinicId ?? ''),
    `/clinics/${clinicId}/branches`,
    { enabled: Boolean(clinicId) && (canManage || canAssignRoles || permissions.has('BRANCH_READ')) },
  );
  const branchName = (branchId: string | undefined) => {
    const branch = (branches.data ?? []).find((candidate) => candidate.id === branchId);
    return branch ? localised.name(branch, branch.code) : '—';
  };

  const invite = useApiMutation<InviteStaffResponse, InviteStaffRequest>(
    (body) => ({ path: `/clinics/${clinicId}/staff`, options: { method: 'POST', body } }),
    {
      invalidates: [queryKeys.staff(clinicId ?? '')],
      onSuccess: () => setInviting(false),
    },
  );

  const endEmployment = useApiMutation<void, string>(
    (employmentId) => ({
      path: `/clinics/${clinicId}/staff/${employmentId}`,
      options: { method: 'DELETE' },
    }),
    { invalidates: [queryKeys.staff(clinicId ?? '')] },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.staff')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader
        title={t('nav.items.staff')}
        description={branchView ? t('staff.descriptionBranch') : t('staff.description')}
        actions={
          canManage ? (
            <button
              type="button"
              className="button button--primary"
              onClick={() => {
                setInviting((open) => !open);
                setNewRole('RECEPTIONIST');
                setIssued(null);
              }}
            >
              {inviting ? t('common.cancel') : t('staff.invite')}
            </button>
          ) : null
        }
      />

      {issued ? <AddedHandover result={issued} onDismiss={() => setIssued(null)} /> : null}

      {resetFor ? (
        <div className="callout" style={{ marginBlockEnd: 'var(--space-5)' }} role="status">
          <p>{t('staff.resetDone', { name: resetFor })}</p>
          <button type="button" className="button button--ghost" onClick={() => setResetFor(null)}>
            {t('common.dismiss')}
          </button>
        </div>
      ) : null}

      {inviting ? (
        <form
          className="panel"
          style={{ marginBlockEnd: 'var(--space-5)' }}
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const branchId = String(form.get('branchId') ?? '');
            invite.mutate(
              {
                email: String(form.get('email') ?? '').trim(),
                displayName: String(form.get('displayName') ?? '').trim(),
                role: newRole,
                branchId: isBranchScoped(newRole) && branchId ? branchId : undefined,
                password: String(form.get('password') ?? ''),
              },
              { onSuccess: (result) => setIssued(result) },
            );
          }}
        >
          <div className="form-grid">
            <label className="field">
              <span className="field__label">{t('staff.email')}</span>
              <input type="email" name="email" className="field__input ltr" required autoComplete="off" />
            </label>
            <label className="field">
              <span className="field__label">{t('staff.name')}</span>
              <input name="displayName" className="field__input" required />
            </label>
            <RoleField value={newRole} onChange={setNewRole} />
            <BranchField role={newRole} branches={branches.data ?? []} />
            <label className="field">
              <span className="field__label">{t('staff.password')}</span>
              <input
                name="password"
                className="field__input ltr"
                required
                minLength={MINIMUM_PASSWORD_LENGTH}
                maxLength={200}
                autoComplete="off"
                spellCheck={false}
              />
              <span className="field__hint">{t('staff.passwordHint', { n: MINIMUM_PASSWORD_LENGTH })}</span>
            </label>
          </div>

          {invite.error ? <ErrorState error={invite.error} /> : null}

          <div style={{ marginBlockStart: 'var(--space-4)' }}>
            <button type="submit" className="button button--primary" disabled={invite.isPending}>
              {invite.isPending ? t('branches.saving') : t('staff.sendInvite')}
            </button>
          </div>
        </form>
      ) : null}

      <AsyncSection
        query={staff}
        skeletonRows={6}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('staff.emptyTitle')}
        emptyDescription={t('staff.emptyDescription')}
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('staff.name')}</th>
                  <th scope="col">{t('staff.email')}</th>
                  <th scope="col">{t('staff.role')}</th>
                  <th scope="col">{t('staff.branch')}</th>
                  <th scope="col">{t('common.status')}</th>
                  {showActions ? <th scope="col">{t('common.actions')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((member) => {
                  // The administrator's seat is Laseera's to change (CR-15): no role change, no
                  // removal, and their password goes through the key icon like anyone's own.
                  const isAdministrator = member.role === 'CLINIC_ADMIN';
                  const live = member.employmentStatus !== 'ENDED';
                  return (
                  <Fragment key={member.id}>
                  <tr>
                    <th scope="row">{member.displayName}</th>
                    <td className="ltr">{member.email}</td>
                    <td>{t(`roles.${member.role ?? 'RECEPTIONIST'}`)}</td>
                    <td>{member.branchId ? branchName(member.branchId) : t('staff.wholeClinic')}</td>
                    <td>
                      <StaffStatus member={member} />
                    </td>
                    {showActions ? (
                      <td>
                        {canSetHours && live && member.role === 'DOCTOR' ? (
                          <Link
                            className="button button--ghost"
                            to={`/doctor-hours?doctor=${encodeURIComponent(member.id ?? '')}`}
                          >
                            {t('staff.hours')}
                          </Link>
                        ) : null}
                        {canAssignRoles && live && !isAdministrator ? (
                          <button
                            type="button"
                            className="button button--ghost"
                            aria-expanded={changingId === member.id}
                            onClick={() => {
                              setChangingId((open) => (open === member.id ? null : (member.id ?? null)));
                              setResettingId(null);
                            }}
                          >
                            {t('staff.changeRole')}
                          </button>
                        ) : null}
                        {/* Your own password is changed from the key icon, where the current one is
                            asked for, so the row offers no shortcut around that. Someone not active
                            here has no sign-in to reset: a pending person is added again instead. */}
                        {canManage &&
                        member.employmentStatus === 'ACTIVE' &&
                        member.accountId !== ownAccountId &&
                        !isAdministrator ? (
                          <button
                            type="button"
                            className="button button--ghost"
                            aria-expanded={resettingId === member.id}
                            onClick={() => {
                              setResettingId((open) => (open === member.id ? null : (member.id ?? null)));
                              setChangingId(null);
                              setResetFor(null);
                            }}
                          >
                            {t('staff.resetPassword')}
                          </button>
                        ) : null}
                        {canManage && live && !isAdministrator ? (
                          <button
                            type="button"
                            className="button button--ghost"
                            disabled={endEmployment.isPending}
                            onClick={() => endEmployment.mutate(member.id ?? '')}
                          >
                            {t('staff.endEmployment')}
                          </button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                  {resettingId === member.id ? (
                    <tr>
                      <td colSpan={showActions ? 6 : 5}>
                        <ResetPasswordForm
                          clinicId={clinicId}
                          member={member}
                          onCancel={() => setResettingId(null)}
                          onDone={() => {
                            setResettingId(null);
                            setResetFor(member.displayName ?? member.email ?? '');
                          }}
                        />
                      </td>
                    </tr>
                  ) : null}
                  {changingId === member.id ? (
                    <tr>
                      <td colSpan={showActions ? 6 : 5}>
                        <ChangeRoleForm
                          clinicId={clinicId}
                          member={member}
                          branches={branches.data ?? []}
                          onCancel={() => setChangingId(null)}
                          onDone={() => setChangingId(null)}
                        />
                      </td>
                    </tr>
                  ) : null}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      {endEmployment.error ? <ErrorState error={endEmployment.error} /> : null}

      {canManage || canAssignRoles ? (
        <p className="field__hint" style={{ marginBlockStart: 'var(--space-4)' }}>
          {t('staff.roleChangeHint')} {t('staff.administratorHint')}
        </p>
      ) : null}
    </>
  );
}

/** The roles a clinic can give, never clinic administrator (CR-15). */
function RoleField({
  value,
  onChange,
}: {
  readonly value: RoleTemplate;
  readonly onChange: (role: RoleTemplate) => void;
}) {
  const { t } = useTranslation();
  return (
    <label className="field">
      <span className="field__label">{t('staff.role')}</span>
      <select
        name="role"
        className="field__input"
        value={value}
        onChange={(event) => onChange(event.target.value as RoleTemplate)}
      >
        {ASSIGNABLE_ROLES.map((role) => (
          <option key={role} value={role}>
            {t(`roles.${role}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Where a role works (ASM-009). Branch managers and receptionists must name one branch, so there is
 * no "whole clinic" choice to make by mistake and the browser will not submit until one is picked.
 * Doctors cover the whole clinic, so for them there is nothing to choose, only a word saying so.
 */
function BranchField({
  role,
  branches,
  defaultValue,
}: {
  readonly role: RoleTemplate;
  readonly branches: readonly BranchResponse[];
  readonly defaultValue?: string;
}) {
  const localised = useLocalisedName();
  const { t } = useTranslation();

  if (!isBranchScoped(role)) {
    return (
      <div className="field">
        <span className="field__label">{t('staff.branch')}</span>
        <p className="field__hint">{t('staff.wholeClinicRole')}</p>
      </div>
    );
  }

  return (
    <label className="field">
      <span className="field__label">{t('staff.branch')}</span>
      <select name="branchId" className="field__input" required defaultValue={defaultValue ?? ''}>
        <option value="" disabled>
          {t('staff.chooseBranch')}
        </option>
        {branches.map((branch) => (
          <option key={branch.id} value={branch.id}>
            {localised.name(branch, branch.code)}
          </option>
        ))}
      </select>
      <span className="field__hint">
        {branches.length === 0 ? t('staff.noBranchesYet') : t('staff.branchHint')}
      </span>
    </label>
  );
}

/**
 * A new role, or a new branch within the same role, for someone who works here.
 *
 * A form rather than a dropdown in the row, because moving a doctor to the desk needs a branch as
 * well as a role, and a dropdown that fired on change could only send a role the server would
 * refuse. Their sessions end when it is saved, as the hint below the table says.
 */
function ChangeRoleForm({
  clinicId,
  member,
  branches,
  onCancel,
  onDone,
}: {
  readonly clinicId: string;
  readonly member: StaffMemberResponse;
  readonly branches: readonly BranchResponse[];
  readonly onCancel: () => void;
  readonly onDone: () => void;
}) {
  const { t } = useTranslation();
  const [role, setRole] = useState<RoleTemplate>(
    member.role && ASSIGNABLE_ROLES.includes(member.role) ? member.role : 'RECEPTIONIST',
  );
  const change = useApiMutation<StaffMemberResponse, ChangeRoleRequest>(
    (body) => ({ path: `/clinics/${clinicId}/staff/${member.id}`, options: { method: 'PATCH', body } }),
    { invalidates: [queryKeys.staff(clinicId)], onSuccess: onDone },
  );

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        const branchId = String(new FormData(event.currentTarget).get('branchId') ?? '');
        change.mutate({ role, branchId: isBranchScoped(role) && branchId ? branchId : undefined });
      }}
    >
      <p className="field__label">{t('staff.changeRoleTitle', { name: member.displayName ?? member.email ?? '' })}</p>
      <div className="form-grid">
        <RoleField value={role} onChange={setRole} />
        <BranchField
          key={role}
          role={role}
          branches={branches}
          defaultValue={isBranchScoped(member.role) ? (member.branchId ?? '') : ''}
        />
      </div>

      {change.error ? <ErrorState error={change.error} /> : null}

      <div style={{ display: 'flex', gap: 'var(--space-3)', marginBlockStart: 'var(--space-3)' }}>
        <button type="submit" className="button button--primary" disabled={change.isPending}>
          {change.isPending ? t('branches.saving') : t('staff.changeRoleSubmit')}
        </button>
        <button type="button" className="button button--ghost" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}

/**
 * A new password for someone who forgot theirs or is locked out.
 *
 * Plain text for the same reason as the add form: the administrator is about to pass it on. The
 * two refusals the server can give are said in the reader's language rather than as the server's
 * English: a doctor who also works at another clinic owns that password (DEC-012), and an
 * administrator's own password is changed from their account.
 */
function ResetPasswordForm({
  clinicId,
  member,
  onCancel,
  onDone,
}: {
  readonly clinicId: string;
  readonly member: StaffMemberResponse;
  readonly onCancel: () => void;
  readonly onDone: () => void;
}) {
  const { t } = useTranslation();
  const reset = useApiMutation<void, ResetPasswordRequest>((body) => ({
    path: `/clinics/${clinicId}/staff/${member.id}/password`,
    options: { method: 'POST', body },
  }));

  const rule = reset.error instanceof ApiError ? reset.error.problem.rule : undefined;
  const refusal = rule === 'DEC-012' ? t('staff.resetShared') : rule === 'FR-105' ? t('staff.resetSelf') : null;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        reset.mutate({ password: String(form.get('password') ?? '') }, { onSuccess: onDone });
      }}
    >
      <label className="field">
        <span className="field__label">
          {t('staff.resetTitle', { name: member.displayName ?? member.email ?? '' })}
        </span>
        <input
          name="password"
          className="field__input ltr"
          required
          minLength={MINIMUM_PASSWORD_LENGTH}
          maxLength={200}
          autoComplete="off"
          spellCheck={false}
        />
        <span className="field__hint">{t('staff.resetHint', { n: MINIMUM_PASSWORD_LENGTH })}</span>
      </label>

      {refusal ? (
        <p className="field__error" role="alert">
          {refusal}
        </p>
      ) : reset.error ? (
        <ErrorState error={reset.error} />
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--space-3)', marginBlockStart: 'var(--space-3)' }}>
        <button type="submit" className="button button--primary" disabled={reset.isPending}>
          {reset.isPending ? t('branches.saving') : t('staff.resetSubmit')}
        </button>
        <button type="button" className="button button--ghost" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}

function StaffStatus({ member }: { readonly member: StaffMemberResponse }) {
  const { t } = useTranslation();

  if (member.employmentStatus === 'ENDED') {
    return <span className="badge badge--muted">{t('staff.status.ENDED')}</span>;
  }
  if (member.invitationPending) {
    return <span className="badge badge--warning">{t('staff.status.INVITED')}</span>;
  }
  if (member.accountStatus === 'LOCKED' || member.accountStatus === 'DISABLED') {
    return <span className="badge badge--danger">{t(`staff.status.${member.accountStatus}`)}</span>;
  }
  return <span className="badge">{t('staff.status.ACTIVE')}</span>;
}

/**
 * What to tell the person just added.
 *
 * Usually that they can sign in now with the password just chosen. When the address already
 * belonged to someone who signs in to Laseera — a doctor who works at another clinic too — the
 * password typed was not used, and the administrator has to know that before they pass it on.
 */
function AddedHandover({
  result,
  onDismiss,
}: {
  readonly result: InviteStaffResponse;
  readonly onDismiss: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="callout" style={{ marginBlockEnd: 'var(--space-5)' }} role="status">
      {result.passwordSet ? (
        <>
          <p className="state__title">
            {t('staff.createdTitle', { name: result.member?.displayName ?? result.member?.email ?? '' })}
          </p>
          <p>{t('staff.createdDescription')}</p>
        </>
      ) : (
        <>
          <p className="state__title">{t('staff.existingAccountTitle')}</p>
          <p>{t('staff.existingAccountDescription')}</p>
        </>
      )}
      <button type="button" className="button button--ghost" onClick={onDismiss}>
        {t('common.dismiss')}
      </button>
    </div>
  );
}
